import React, { useEffect, useMemo, useRef, useState } from "react"
import { connect } from "react-redux"
import moment from "moment-timezone"
import { Link, Prompt } from "react-router-dom"
import { Alert, Container, Spinner } from "reactstrap"
import Breadcrumbs from "components/Common/Breadcrumb"
import { SPORTS, countryTimezone, readPlannerQuery, writePlannerQuery } from "./plannerQuery"
import { todayIn } from "./plannerLabels"
import { generationErrors, generationRequest, newGenerationForm, periodOf, seasonOf } from "./generationRequest"
import { plannedMeanwhileLine, savedLine } from "./generationLabels"
import { plural } from "./plannerLabels"
import PlannerDialog from "./PlannerDialog"
import { UNCONFIRMED } from "./proposalMessages"
import useProposalOperation from "./useProposalOperation"
import useGeneration, { chosenRow } from "./useGeneration"
import GenerationForm from "./GenerationForm"
import GenerationReview from "./GenerationReview"
import "./eventsPlanner.scss"

// „Generate proposals” (wayfinder ticket 17; Phase D4): the drafts of a period made from the last seasons' tournaments, by organizer or by
// period, reviewed before they are saved as Ciorne in one batch. Opened from the Events Planner on its country and season (SuperAdmin only,
// cpAccessPolicy.js).
export function GenerateProposals({ settings = {}, history, location }) {
  const localToday = moment(Date.now()).format("YYYY-MM-DD")
  const planner = useMemo(() => readPlannerQuery(location.search, localToday), [location.search, localToday])
  const today = todayIn(countryTimezone(planner.country))
  const [form, setForm] = useState(() => newGenerationForm(planner, today))
  const [shownErrors, setShownErrors] = useState(false)
  const errors = generationErrors(form)
  const generation = useGeneration()
  const [message, setMessage] = useState(null)
  // While the review's latest choices wait for the server, their ticks show at once.
  const { answer, status, choices } = generation
  const shown = useMemo(() => (answer && status === "updating" ? { ...answer, rows: answer.rows.map(row => chosenRow(row, choices[row.id])) } : answer),
    [answer, status, choices])

  const generate = () => {
    setShownErrors(true)
    if (Object.keys(errors).length) return
    setMessage(null)
    generation.run(generationRequest(form))
  }

  // Save (ticket 17): one create batch, all or nothing, of the ticked rows' drafts in calendar order (Phase D3). The drafts are those of
  // the answer shown, so Save waits while a change is being read.
  const operation = useProposalOperation()
  const [saved, setSaved] = useState(null)
  const drafts = answer ? answer.rows.filter(row => row.ticked && row.proposal).map(row => row.proposal) : []
  const save = async () => {
    setMessage(null)
    const result = await operation.run("create", "/admin/tournament-proposals", { proposals: drafts })
    if (!result || ["cancelled", "busy"].includes(result.status)) return
    if (result.status === "saved") {
      setSaved(result.proposals || [])
      return
    }
    if (result.code === "PROPOSAL_SOURCE_ALREADY_PLANNED") {
      const rows = result.details?.rows || []
      setMessage({ tone: "warning", text: plannedMeanwhileLine(rows) })
      generation.untick(rows.map(({ rowId }) => rowId))
      return
    }
    setMessage({ tone: "danger", text: result.status === "unknown" ? result.message || UNCONFIRMED : result.message })
  }
  const again = () => {
    setSaved(null)
    setMessage(null)
    generation.reset()
  }
  // Leaving with drafts not saved asks first (ticket 17), in the page (CreateEvent's pattern), as going back to the form does.
  const unsaved = !saved && answer ? answer.rows.filter(row => row.ticked).length : 0
  const leaving = useRef(false)
  // What discarding leads to: the address the SuperAdmin was leaving for, or back to the form (`target` null).
  const [discarding, setDiscarding] = useState(null)
  useEffect(() => {
    if (!unsaved) return undefined
    const beforeUnload = event => { event.preventDefault(); event.returnValue = "" }
    window.addEventListener("beforeunload", beforeUnload)
    return () => window.removeEventListener("beforeunload", beforeUnload)
  }, [unsaved])
  const discard = () => {
    const exit = discarding
    setDiscarding(null)
    if (!exit.target) return again()
    leaving.current = true
    return exit.action === "REPLACE" ? history.replace(exit.target) : history.push(exit.target)
  }
  // The planner on the run's season, its new drafts listed (and its organizer, by organizer).
  const plannerPath = () => "/events-planner" + writePlannerQuery({ ...planner, season: seasonOf(periodOf(form).from), view: "list", queue: "drafts",
    sports: form.mode === "period" ? form.sports : [...SPORTS], organizers: form.mode === "organizer" ? [form.organizer.id] : planner.organizers })

  return <div className="page-content events-planner">
    <Container fluid>
      <Breadcrumbs title="Events Planner" path={"/events-planner" + location.search} breadcrumbItem="Generate proposals" />
      <div className="ep-root">
        <Prompt when={unsaved > 0} message={(target, action) => {
          if (leaving.current) return true
          setDiscarding({ target, action })
          return false
        }} />
        {message && <Alert color={message.tone} className="mb-0">{message.text}</Alert>}
        {saved && <section className="ep-card ep-stack ep-gap" aria-label="Drafts saved">
          <Alert color="success" className="mb-0">{savedLine(saved)}</Alert>
          <span className="ep-meta">They are Drafts: nothing reaches an organizer until it is sent from To send.</span>
          <span className="ep-generate-actions">
            <Link className="btn btn-primary" to={plannerPath()}>Show in calendar</Link>
            <button type="button" className="btn btn-light" onClick={again}>Generate again</button>
          </span>
        </section>}
        {!saved && !generation.answer && <GenerationForm form={form} errors={shownErrors ? errors : {}} settings={settings} today={today}
          busy={generation.status === "loading"} onChange={setForm} onGenerate={generate} />}
        {generation.status === "loading" && <div className="ep-loading"><Spinner size="sm" /> Generating the drafts…</div>}
        {generation.error && <Alert color="danger" className="mb-0">{generation.error}{answer && <>{" "}
          <button type="button" className="btn btn-link p-0 align-baseline" onClick={generation.retry}>Read the drafts again</button></>}</Alert>}
        {!saved && shown && <GenerationReview answer={shown} settings={settings} timeZone={countryTimezone(form.country)} planner={planner}
          updating={status === "updating"} onChoose={generation.choose} onSave={save}
          saving={Boolean(operation.pending)} canSave={status === "ready" && !operation.pending} onDiscard={() => setDiscarding({ target: null })} />}
        {discarding && <PlannerDialog title={`Discard ${plural(unsaved, "draft")}?`} sub="Nothing is saved yet: the review and its changes are lost."
          closeLabel="Keep reviewing" onClose={() => setDiscarding(null)}
          actions={<button type="button" className="btn btn-danger" onClick={discard}>Discard drafts</button>} />}
      </div>
    </Container>
  </div>
}

export default connect(({ session: { settings } = {} }) => ({ settings }))(GenerateProposals)
